import { generatePath } from 'react-router-dom';

import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { AdditionalInformation } from '@/api/Claim/getAdditionalInformationEntitySummaryQuery';
import {
  AdditionalClaimInformation,
  ClaimGroupConfirmation,
} from '@/api/Claim/getClaimGroupConfirmationQuery';
import { useGetMultiAdditionalInformationEntitySummaryQuery } from '@/view/app/hooks/useGetMultiAdditionalInformationEntitySummaryQuery';
import { useGetOrderDetailsQuery } from '@/view/app/hooks/useGetOrderDetailsQuery';
import { useGetSolutionsByClaim } from '@/view/app/hooks/useGetSolutionsByClaim';

import { FINAL_ROUTES } from '../../app';
import { IrshStepValues } from '../../newDesignPages/irshPages/IrshPages.types';

import {
  getMultiAdditionalInformationEnumValue,
  getMultiAdditionalInformationStringValue,
  getMultiAdditionalInformationValue,
} from './ResumeCard.helpers';

type AdditionalInformationFileValue = NonNullable<
  AdditionalClaimInformation['additionalInformationFilesValue']
>[number];

const hasVisibleFileName = (
  file: AdditionalInformationFileValue,
): file is AdditionalInformationFileValue & { fileName: string } =>
  Boolean(file.customerVisibility && file.fileName);

export const useResumeCard = ({
  claimGroupConfirmation,
}: {
  claimGroupConfirmation?: ClaimGroupConfirmation;
}) => {
  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();

  const claimGroupId = values?.claimGroupId;
  const claims =
    values?.claims?.map((claim) => {
      const additionalClaimInformation =
        claimGroupConfirmation?.claims.find((c) => c.claimId === claim.claimId)
          ?.additionalClaimInformation ?? [];

      return {
        ...claim,
        additionalClaimInformation,
      };
    }) || [];

  const ownerProductIds = values?.claims?.map((claim) => claim.ownerProductId);
  const additionalInformationIds = claimGroupConfirmation?.claims
    .flatMap((claim) =>
      claim?.additionalClaimInformation?.map(
        (additionalInformation) =>
          additionalInformation.additionalInformationId,
      ),
    )
    .reduce((acc: string[], curr) => {
      if (!acc.includes(curr)) {
        return [...acc, curr];
      }

      return acc;
    }, []);

  const { orderDetail } = useGetOrderDetailsQuery({
    orderId: values?.order?.orderId,
  });

  const products = orderDetail?.products.filter((product) =>
    ownerProductIds?.includes(product.ownerProductId),
  );

  const { additionalInformations } =
    useGetMultiAdditionalInformationEntitySummaryQuery({
      additionalInformationIds,
    });

  const { diagnostic_magasin } = getMultiAdditionalInformationValue({
    additionalInformations,
    additionalClaimInformation:
      claimGroupConfirmation?.claims[0]?.additionalClaimInformation,
    comparaisonRule: 'uniqueName',
    compareValues: ['diagnostic_magasin'],
  });

  const additionalInformationStringIdsLabels =
    getMultiAdditionalInformationStringValue({
      additionalInformations,
      additionalClaimInformation:
        claimGroupConfirmation?.claims[0]?.additionalClaimInformation,
    });

  const additionalInformationIdsLabels = getMultiAdditionalInformationEnumValue(
    {
      additionalInformations,
      additionalClaimInformation:
        claimGroupConfirmation?.claims[0]?.additionalClaimInformation,
    },
  );

  const allInformations = useGetMultiAdditionalInformationEntitySummaryQuery({
    additionalInformationIds: additionalInformationIdsLabels?.map(
      (info) => info.enumValue,
    ),
  });

  const pairLabelValue = allInformations.additionalInformations?.map(
    (inforamtion: AdditionalInformation) => ({
      label: additionalInformationIdsLabels.find(
        (idAndLabel) => idAndLabel.enumValue === inforamtion.id,
      )?.label,
      value: inforamtion.label,
    }),
  );

  const claimAdditionalInformationDocuments: string[] =
    claimGroupConfirmation?.claims[0]?.additionalClaimInformation?.flatMap(
      (additionalInformation: AdditionalClaimInformation) => {
        const singleFileDocumentNames =
          additionalInformation.additionalInformationFileValue
            ?.customerVisibility &&
          additionalInformation.additionalInformationFileValue?.fileName
            ? [additionalInformation.additionalInformationFileValue.fileName]
            : [];

        const multiFilesDocumentNames =
          additionalInformation.additionalInformationFilesValue
            ?.filter(hasVisibleFileName)
            .map((file) => file.fileName) ?? [];

        return [...singleFileDocumentNames, ...multiFilesDocumentNames];
      },
    ) ?? [];

  const documents =
    claimGroupConfirmation?.claims[0]?.documents
      .filter((document) => document.customerVisibility)
      .map((document) => document.name) || [];

  const { solutions } = useGetSolutionsByClaim({
    claimGroupId: claimGroupConfirmation?.claimGroupId,
  });

  const firstClaim = claims?.at(0);

  const linkModify = firstClaim
    ? generatePath(FINAL_ROUTES.CLAIM_GROUP_REASON, {
        claimGroupId,
      })
    : undefined;

  const orderReference = orderDetail?.orderReference;

  const issueLabel =
    claimGroupConfirmation?.claims?.at(0)?.issueName ?? values?.issue?.name;

  const hasOwnerName = Boolean(
    !orderDetail?.isAnonymous &&
      (orderDetail?.firstName || orderDetail?.lastName),
  );
  const customerName = hasOwnerName
    ? [orderDetail?.firstName, orderDetail?.lastName].filter(Boolean).join(' ')
    : undefined;

  return {
    products,
    claims: claims || [],
    description: diagnostic_magasin,
    pairLabelValue,
    pairStringKeyValues: additionalInformationStringIdsLabels,
    documents: [...claimAdditionalInformationDocuments, ...documents],

    solution: solutions?.find(
      (solution) =>
        solution.solutionGroupId === claimGroupConfirmation?.solutionGroupId,
    ),
    linkModify,
    orderReference,
    customerName,
    issueLabel,
  };
};
